import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { api } from "./api";

type Session = {
  token: string;
  expiresAt: number;
  // Parsed from JWT claims for in-app UI gates.
  sub?: string;
  email?: string;
  tenantId?: string;
  tenantSlug?: string;
  roles: string[];
};

type AuthCtx = {
  session: Session | null;
  login: (tenantSlug: string, email: string, password: string) => Promise<void>;
  logout: () => void;
};

const Ctx = createContext<AuthCtx | null>(null);

// localStorage is the persistence — survives page reload but lives only
// on this device. Tokens are short-TTL (12h default) and re-issued via
// /auth/token, so leaked-LS surface is bounded.
const STORAGE_KEY = "juris.session";

function decodeJwt(tok: string): Record<string, unknown> {
  try {
    const [, payload] = tok.split(".");
    const padded = payload.padEnd(payload.length + ((4 - (payload.length % 4)) % 4), "=");
    return JSON.parse(atob(padded.replace(/-/g, "+").replace(/_/g, "/"))) as Record<string, unknown>;
  } catch {
    return {};
  }
}

function sessionFromToken(token: string): Session {
  const c = decodeJwt(token);
  const exp = typeof c.exp === "number" ? c.exp : 0;
  const ra = c.realm_access as { roles?: string[] } | undefined;
  return {
    token,
    expiresAt: exp * 1000,
    sub: c.sub as string | undefined,
    email: c.email as string | undefined,
    tenantId: c.tenant_id as string | undefined,
    roles: ra?.roles ?? [],
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(() => {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    try {
      const s = JSON.parse(raw) as Session;
      if (s.expiresAt > Date.now()) return s;
    } catch {
      // fallthrough
    }
    return null;
  });

  useEffect(() => {
    if (session) localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    else localStorage.removeItem(STORAGE_KEY);
  }, [session]);

  const login = useCallback(async (tenantSlug: string, email: string, password: string) => {
    const res = await api("/auth/token", {
      method: "POST",
      body: JSON.stringify({ tenantSlug, email, password }),
    });
    if (!res.ok) throw new Error("invalid credentials");
    const body = (await res.json()) as { access_token: string };
    setSession({ ...sessionFromToken(body.access_token), tenantSlug });
  }, []);

  const logout = useCallback(() => {
    // Best-effort server notify so the access log records the logout
    // event; the embedded RS256 issuer mints stateless JWTs so the
    // canonical invalidation is still the localStorage discard.
    const token = session?.token;
    if (token) {
      void api("/auth/logout", { method: "POST", token }).catch(() => {});
    }
    setSession(null);
  }, [session]);

  const value = useMemo(() => ({ session, login, logout }), [session, login, logout]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthCtx {
  const c = useContext(Ctx);
  if (!c) throw new Error("useAuth: AuthProvider missing");
  return c;
}
